import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import heroIllustration from "@/assets/hero-illustration.gif";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import WaitlistDialog from "./WaitlistDialog";

interface HeadlineWord {
  text: string;
  italic: boolean;
}

interface HeroBassProps {
  variant?: 'default' | 'waitlist';
  headlineWords?: HeadlineWord[];
  subtitle?: string;
  onGetStartedClick?: () => void;
  queued?: boolean;
  /** Accepted for call-site compatibility; the hero is one responsive tree. */
  mobileRedesign?: boolean;
}

const HeroBass = ({
  variant = 'default',
  headlineWords,
  subtitle,
  onGetStartedClick,
  queued = false,
}: HeroBassProps) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const isMobile = useIsMobile();

  const hero = (
    <section className="relative bg-[#B45309] overflow-hidden min-h-0 md:min-h-[90vh] flex flex-col justify-center">
      {/* Background Depth - Original Orange Tones */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_#D97706,_#B45309_60%,_#78350F_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(255,255,255,0.05),transparent_60%)]" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.15] mix-blend-overlay" />
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[60px] md:h-[120px] pointer-events-none z-20">
        <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1440 120" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,60 Q360,0 720,60 T1440,60 L1440,120 L0,120 Z" fill="#FFF7F0" />
        </svg>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 sm:px-8 relative z-10 w-full">
        <div className="relative flex flex-col items-center pt-14 pb-[120px] md:py-20 lg:py-32 text-center">
          
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.12, delayChildren: 0.1 }
              }
            }}
            className="mb-5 sm:mb-12 pt-0 sm:pt-24 lg:pt-32 w-full flex justify-center mx-auto relative z-10"
          >
            <h1 className="text-[#F5F5F4] text-[44px] sm:text-5xl md:text-6xl lg:text-[72px] font-medium font-playfair leading-[1.05] tracking-[-0.015em] sm:tracking-tight [text-wrap:balance] flex flex-wrap justify-center gap-x-[0.25em]">
              {(headlineWords || [
                { text: "Let", italic: false },
                { text: "the", italic: false },
                { text: "experts", italic: false },
                { text: "style", italic: true },
                { text: "you.", italic: false },
              ]).map((word, i) => (
                <motion.span
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 30, filter: "blur(12px)" },
                    visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 1, ease: [0.2, 0.65, 0.3, 0.9] } }
                  }}
                  className={`inline-block ${word.italic ? "italic text-white drop-shadow-sm" : ""}`}
                >
                  {word.text}
                </motion.span>
              ))}
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="max-w-2xl mx-auto mb-8 sm:mb-16 flex flex-col relative z-10"
          >
            <p className="text-[#FDE7CC] sm:text-white/95 text-[12px] sm:text-[13px] uppercase tracking-[0.25em] font-semibold drop-shadow-sm leading-[1.7] sm:leading-relaxed">
              {subtitle || "Shopping and styling? Outsource it to an expert."}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
            className="flex flex-col items-center gap-3.5 sm:gap-6 w-full relative z-10 mb-0 sm:mb-16 md:mb-24 lg:mb-32"
          >
            {variant === 'waitlist' ? (
              <div className="flex flex-col w-full max-w-lg mx-auto">
                <div className="mb-8 w-full flex flex-col items-center">
                  <p className="text-white/90 text-center text-[15px] sm:text-[16px] leading-relaxed font-playfair tracking-wide max-w-sm">
                    We are full at the moment.<br className="hidden sm:block" /> Join the waitlist for early access.
                  </p>
                </div>
                <form
                  onSubmit={async e => {
                    e.preventDefault();
                    if (!email) {
                      toast.error("Please enter your email");
                      return;
                    }
                    setIsSubmitting(true);
                    try {
                      const { error } = await supabase.from('waitlist_submissions').insert([{
                        email,
                        name: '',
                        comments: ''
                      }] as any);
                      if (error) throw error;
                      setHasSubmitted(true);
                      setEmail('');
                    } catch (error) {
                      console.error('Error submitting to waitlist:', error);
                      toast.error("Something went wrong. Please try again.");
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full"
                >
                  {!hasSubmitted ? (
                    <>
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full h-[52px] px-6 text-[13px] bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/30 focus:bg-white/10 transition-all rounded-md"
                        disabled={isSubmitting}
                      />
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-white hover:bg-stone-200 text-[#0a0502] px-8 h-[52px] rounded-md text-[12px] font-bold uppercase tracking-[0.15em] flex items-center justify-center shadow-lg transition-all w-full sm:w-auto overflow-hidden whitespace-nowrap border-b-2 border-stone-300 active:border-b-0 active:translate-y-[2px]"
                      >
                        {isSubmitting ? 'Submitting...' : 'Request Access'}
                      </Button>
                    </>
                  ) : (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="w-full h-[52px] bg-white/10 border border-white/20 rounded-md flex items-center justify-center text-white text-sm font-medium tracking-wide shadow-inner backdrop-blur-sm"
                    >
                      <svg className="w-5 h-5 mr-2 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      You're on the list. We'll be in touch soon.
                    </motion.div>
                  )}
                </form>
                {!hasSubmitted && (
                  <p className="text-white/50 text-[10px] uppercase tracking-widest mt-4 text-center">
                    Join over 2,000+ waitlisted members.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-6 w-full sm:w-auto">
                <div className="flex flex-col sm:flex-row items-center gap-3.5 sm:gap-4 w-full sm:w-auto">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full sm:w-auto"
                  >
                    <Button
                      onClick={() => onGetStartedClick ? onGetStartedClick() : navigate('/auth', { state: { tab: 'signup' } })}
                      className={`px-10 h-[52px] rounded-md text-[12px] font-bold uppercase tracking-[0.15em] flex items-center justify-center shadow-xl transition-all w-full sm:w-auto border-b-2 active:border-b-0 active:translate-y-[2px] ${queued ? "bg-[#16A34A] hover:bg-[#15803D] text-white border-[#0f7a37]" : "bg-white hover:bg-stone-200 text-[#0f172a] md:text-[#0a0502] border-stone-300"}`}
                    >
                      {queued ? "You're on the list" : "Join the waitlist"}
                    </Button>
                  </motion.div>
                </div>
              </div>
            )}

            <Button 
                onClick={() => setShowVideo(true)} 
                className="mt-0 sm:mt-6 w-full sm:w-auto h-12 sm:h-10 bg-white/10 hover:bg-white/20 text-white backdrop-blur-[8px] sm:backdrop-blur-md border border-white/30 rounded-full px-8 py-0 sm:py-3 text-[15px] sm:text-[16px] font-medium flex items-center justify-center gap-2.5 sm:gap-2 transition-all duration-300 hover:scale-105"
            >
                <span className="sm:hidden inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-white text-[#B45309]">
                  <Play className="!h-2.5 !w-2.5 fill-current ml-px" />
                </span>
                <Play className="hidden sm:block w-5 h-5 fill-current" />
                Watch the Cello Experience
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );

  // The promo plays from our own storage, not YouTube: the site's CSP blocked the
  // YouTube iframe in production, and a native player works inline on a phone
  // with no third-party chrome. Two encodes of the same 62s cut (2026-09-25,
  // Vidico "Premium Promo"): 1080p ~20MB for desktop, 720p ~10MB below md. The
  // poster is in the repo — the videos bucket only takes video MIME types.
  const promoSrc = supabase.storage
    .from("videos")
    .getPublicUrl(isMobile ? "marketing/cello-experience-720.mp4" : "marketing/cello-experience-1080.mp4").data.publicUrl;

  const videoDialog = (
    <Dialog open={showVideo} onOpenChange={setShowVideo}>
      <DialogContent className="max-w-5xl bg-black/95 border-white/10 p-2 sm:p-4 [&>button]:text-white">
        <DialogHeader className="sr-only">
          <DialogTitle>Cello Promo Video</DialogTitle>
        </DialogHeader>
        <div className="relative pt-[56.25%] w-full rounded-lg overflow-hidden bg-black flex items-center justify-center shadow-2xl">
          {/* Mounted only while the dialog is open (Radix unmounts closed content), so
              autoPlay follows the click that opened it and the source is picked for
              the width it opened at. */}
          <video
            src={promoSrc}
            poster="/videos/cello-experience-poster.jpg"
            controls
            autoPlay
            playsInline
            preload="metadata"
            aria-label="Cello promo video"
            className="absolute top-0 left-0 h-full w-full bg-black"
          />
        </div>
      </DialogContent>
    </Dialog>
  );

  // The hero is one responsive tree at every width. `mobileRedesign` stays on
  // the props (accepted, ignored) so the splash pages' call sites don't change.
  return (
    <>
      {hero}
      {videoDialog}
    </>
  );
};

export default HeroBass;